import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles, Plus, Loader2, Trash2, FileText, MessageSquare, ArrowRight, Flame, MapPin, Archive,
  Clock, ExternalLink, ArchiveRestore, UserCheck, Calendar as CalendarIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCrmLabels, fmtReminder } from "@/pages/pipeline/crmConfig";
import { ReminderDialog } from "@/pages/pipeline/PipelineDialogs";
import ContactSearchInput from "@/components/ContactSearchInput";

const MANAGER_ROLES = new Set(["director_sales", "dir_operaciones", "socio_director", "dir_admin_general"]);
const COLS = ["nuevo", "calificando", "calificado", "cotizado"];
const ALL_STATUSES = [...COLS, "archivado"];
const SOURCES = ["Manual", "Web", "Maya", "Recomendación", "Instagram", "Email", "Teléfono", "Partner", "Otro"];
const DOT = {
  nuevo: "bg-sky-500", calificando: "bg-indigo-500", calificado: "bg-emerald-500",
  cotizado: "bg-amber-500", archivado: "bg-zinc-400",
};
const SCORE = {
  ALTO: "border-rose-200 bg-rose-50 text-rose-700",
  MEDIO: "border-amber-200 bg-amber-50 text-amber-700",
  BAJO: "border-zinc-200 bg-zinc-50 text-zinc-600",
  PENDIENTE: "border-zinc-200 bg-zinc-50 text-zinc-500",
};

// "Hoy 14:30" / "Ayer 09:15" / "Hace 3 días" / "25 jul" / "25 jul 2025"
function fmtLeadDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const hhmm = d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const startOfDay = (dt) => new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
  const daysDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (daysDiff === 0) return `Hoy ${hhmm}`;
  if (daysDiff === 1) return `Ayer ${hhmm}`;
  if (daysDiff < 7) return `Hace ${daysDiff} días`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", ...(sameYear ? {} : { year: "numeric" }) });
}
function fmtLeadDateFull(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function LeadCard({ lead, isNew, isManager, onOpen, onDragStart, onDragEnd, isDragging }) {
  return (
    <div
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; onDragStart?.(); }}
      onDragEnd={() => onDragEnd?.()}
      onClick={() => onOpen(lead)}
      className={`group relative cursor-pointer overflow-hidden rounded-lg border bg-card p-3 shadow-sm transition-shadow hover:shadow-md ${isNew ? "ring-2 ring-primary/60" : ""} ${isDragging ? "opacity-40 ring-2 ring-primary" : ""}`}
      data-testid={`crmlead-card-${lead.id}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-heading text-sm font-semibold leading-tight">{lead.name}</p>
        <Badge variant="outline" className={`shrink-0 text-[9px] ${SCORE[lead.score] || SCORE.PENDIENTE}`}>
          {lead.score === "ALTO" && <Flame className="mr-0.5 h-2.5 w-2.5" />}{lead.score || "—"}
        </Badge>
      </div>
      {lead.created_at && (
        <p className="mt-0.5 text-[10px] text-muted-foreground" title={fmtLeadDateFull(lead.created_at)} data-testid={`crmlead-created-${lead.id}`}>
          {fmtLeadDate(lead.created_at)}
        </p>
      )}
      {isNew && (
        <Badge className="mt-1.5 animate-pulse bg-primary text-[9px] text-primary-foreground" data-testid={`crmlead-new-badge-${lead.id}`}>
          Nuevo
        </Badge>
      )}
      {lead.destination && <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground"><MapPin className="h-3 w-3" />{lead.destination}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {lead.source && <Badge variant="outline" className="text-[10px]">{lead.source}</Badge>}
        {lead.cotizacion_id && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-700"><FileText className="mr-1 h-3 w-3" />Cotizado</Badge>}
        {(lead.conversation?.length > 0) && <Badge variant="outline" className="text-[10px]"><MessageSquare className="mr-1 h-3 w-3" />Maya</Badge>}
      </div>
      {isManager && lead.owner_name && (
        <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`crmlead-owner-${lead.id}`}>
          <UserCheck className="h-3 w-3" />{lead.owner_name}
        </p>
      )}
      {lead.next_action_at && (
        <div className="mt-2 flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] text-indigo-700" data-testid={`crmlead-nextaction-${lead.id}`}>
          <Clock className="h-3 w-3 shrink-0" /><span className="truncate">{fmtReminder(lead.next_action_at)}</span>
        </div>
      )}
    </div>
  );
}

function NewLeadDialog({ open, onClose, isManager, agents }) {
  const qc = useQueryClient();
  const { kanban } = useCrmLabels();
  const EMPTY_LEAD = useMemo(() => ({ name: "", email: "", phone: "", destination: "", budget: "", dates: "", brief: "", status: "nuevo", source: "Manual", owner_id: "none" }), []);
  const [f, setF] = useState(EMPTY_LEAD);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { if (open) setF(EMPTY_LEAD); }, [open, EMPTY_LEAD]);
  const mut = useMutation({
    mutationFn: async () => {
      const { owner_id, ...rest } = f;
      const body = { ...rest };
      if (isManager && owner_id && owner_id !== "none") body.owner_id = owner_id;
      return (await api.post("/crm-leads", body)).data;
    },
    onSuccess: (d) => {
      toast.success(d?.owner_name ? `Lead creado y asignado a ${d.owner_name}` : "Lead creado");
      qc.invalidateQueries({ queryKey: ["crm-leads"] });
      qc.invalidateQueries({ queryKey: ["crm-leads-assigned-new"] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="crmlead-new-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />Nuevo lead</DialogTitle>
          <DialogDescription className="text-xs">Se crea como cliente unificado (prospect). Sin duplicar.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Nombre *</Label>
            <ContactSearchInput
              value={f.name}
              onChange={(v) => set("name", v)}
              onSelect={(c) => setF((s) => ({ ...s, name: c.name, email: s.email || c.email || "", phone: s.phone || c.phone || "", destination: s.destination || c.destination || "" }))}
              placeholder="Busca un contacto existente o escribe uno nuevo…"
              testid="crmlead-f-name"
            />
            <p className="mt-1 text-[11px] text-muted-foreground">Si ya está en el sistema, elígelo de la lista; si no, escribe el nombre.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Etapa</Label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="crmlead-f-status"><SelectValue /></SelectTrigger>
                <SelectContent>{COLS.map((s) => <SelectItem key={s} value={s}>{kanban[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fuente</Label>
              <Select value={f.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="crmlead-f-source"><SelectValue /></SelectTrigger>
                <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="crmlead-f-email" /></div>
            <div><Label className="text-xs">Teléfono</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="crmlead-f-phone" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Destino</Label><Input value={f.destination} onChange={(e) => set("destination", e.target.value)} data-testid="crmlead-f-dest" /></div>
            <div><Label className="text-xs">Fechas</Label><Input value={f.dates} onChange={(e) => set("dates", e.target.value)} data-testid="crmlead-f-dates" /></div>
          </div>
          <div><Label className="text-xs">Notas / brief</Label><Textarea rows={2} value={f.brief} onChange={(e) => set("brief", e.target.value)} data-testid="crmlead-f-brief" /></div>
          {isManager && (
            <div>
              <Label className="text-xs">Agente asignado</Label>
              <Select value={f.owner_id || "none"} onValueChange={(v) => set("owner_id", v)}>
                <SelectTrigger data-testid="crmlead-f-owner"><SelectValue placeholder="— Sin asignar —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sin asignar —</SelectItem>
                  {(agents || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-[11px] text-muted-foreground">Si lo asignas a un compañero, le llegará el aviso «te han asignado un nuevo lead» en su panel.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => mut.mutate()} disabled={!f.name || mut.isPending} data-testid="crmlead-f-save">
            {mut.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />}Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeadDrawer({ lead, kanban, phases, isManager, agents, onClose, onRemind }) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState("interesado");
  const setStatus = useMutation({
    mutationFn: async (status) => (await api.patch(`/crm-leads/${lead.id}`, { status })).data,
    onSuccess: (_, status) => { toast.success(`Movido a ${kanban[status]}`); qc.invalidateQueries({ queryKey: ["crm-leads"] }); if (status === "archivado") onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const assignMut = useMutation({
    mutationFn: async (owner_id) => (await api.post(`/crm-leads/${lead.id}/assign`, { owner_id: owner_id === "none" ? null : owner_id })).data,
    onSuccess: (_, owner_id) => {
      toast.success(owner_id === "none" ? "Lead sin asignar" : "Lead asignado");
      qc.invalidateQueries({ queryKey: ["crm-leads"] });
      qc.invalidateQueries({ queryKey: ["crm-leads-assigned-new"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const convert = useMutation({
    mutationFn: async () => (await api.post(`/crm-leads/${lead.id}/convert-to-cotizacion`)).data,
    onSuccess: () => { toast.success("Cotización creada · disponible en Cotizador"); qc.invalidateQueries({ queryKey: ["crm-leads"] }); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const toPipeline = useMutation({
    mutationFn: async () => (await api.post(`/crm-leads/${lead.id}/to-pipeline`, { status: phase })).data,
    onSuccess: () => { toast.success(`Pasado al Pipeline · ${phases.find((p) => p.v === phase)?.l}`); qc.invalidateQueries({ queryKey: ["crm-leads"] }); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/crm-leads/${lead.id}`)).data,
    onSuccess: () => { toast.success("Lead eliminado"); qc.invalidateQueries({ queryKey: ["crm-leads"] }); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="crmlead-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {lead.name}
            <Badge variant="outline" className={`text-[10px] ${SCORE[lead.score] || SCORE.PENDIENTE}`}>{lead.score || "—"}</Badge>
          </DialogTitle>
          <DialogDescription className="flex flex-wrap gap-3 text-xs">
            {lead.email ? (
              <a href={`mailto:${lead.email}`} className="underline decoration-dotted underline-offset-2 hover:text-foreground" data-testid="crmlead-drawer-email">✉ {lead.email}</a>
            ) : <span>sin email</span>}
            {lead.phone && (
              <a href={`tel:${lead.phone}`} className="underline decoration-dotted underline-offset-2 hover:text-foreground" data-testid="crmlead-drawer-phone">📞 {lead.phone}</a>
            )}
            <span>· {lead.source}</span>
            {lead.created_at && (
              <span className="flex items-center gap-1" data-testid="crmlead-drawer-created">
                · <CalendarIcon className="h-3 w-3" /> Chat recibido: <strong>{fmtLeadDateFull(lead.created_at)}</strong>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2 text-sm">
          <div><span className="text-xs text-muted-foreground">Destino</span><p>{lead.destination || "—"}</p></div>
          <div><span className="text-xs text-muted-foreground">Fechas</span><p>{lead.dates || "—"}</p></div>
          <div><span className="text-xs text-muted-foreground">Viajeros</span><p>{lead.travelers ?? "—"}</p></div>
          <div><span className="text-xs text-muted-foreground">Presupuesto</span><p>{lead.budget || "—"}</p></div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Etapa</Label>
            <Select value={lead.status} onValueChange={(v) => setStatus.mutate(v)}>
              <SelectTrigger className="mt-1 h-9" data-testid="crmlead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{kanban[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {isManager ? (
            <div className="sm:col-span-2">
              <Label className="text-xs">Agente asignado</Label>
              <Select value={lead.owner_id || "none"} onValueChange={(v) => assignMut.mutate(v)} disabled={assignMut.isPending}>
                <SelectTrigger className="mt-1 h-9" data-testid="crmlead-assign-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Sin asignar —</SelectItem>
                  {(agents || []).map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ) : lead.owner_name ? (
            <div className="sm:col-span-2">
              <span className="text-xs text-muted-foreground">Agente asignado</span>
              <p className="flex items-center gap-1.5" data-testid="crmlead-drawer-owner"><UserCheck className="h-3.5 w-3.5 text-muted-foreground" />{lead.owner_name}</p>
            </div>
          ) : null}
        </div>

        {lead.next_action_at && (
          <button type="button" onClick={() => lead.gcal_link ? window.open(lead.gcal_link, "_blank") : onRemind(lead)}
            className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-left text-sm text-indigo-700 transition-colors hover:bg-indigo-100" data-testid="crmlead-drawer-nextaction">
            <Clock className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">Próxima acción: {fmtReminder(lead.next_action_at)}{lead.next_action_note ? ` · ${lead.next_action_note}` : ""}</span>
            {lead.gcal_link && <ExternalLink className="h-3.5 w-3.5 shrink-0" />}
          </button>
        )}

        {lead.summary && (
          <div className="rounded-lg border bg-muted/30 p-3">
            <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Resumen de Maya</p>
            <p className="mt-1 text-sm whitespace-pre-wrap">{lead.summary}</p>
          </div>
        )}

        {lead.mailchimp?.id && (
          <div className="rounded-lg border bg-amber-50 p-3" data-testid="crmlead-mailchimp">
            <p className="text-[10px] uppercase tracking-wide text-amber-900">Mailchimp</p>
            <div className="mt-1 flex flex-wrap gap-2 text-xs">
              {lead.mailchimp.status && (
                <Badge variant="outline" className="border-amber-300 bg-white text-amber-900">
                  Estado: {lead.mailchimp.status}
                </Badge>
              )}
              {lead.mailchimp.gender && (
                <Badge variant="outline" className="border-amber-300 bg-white text-amber-900">
                  Género: {lead.mailchimp.gender}
                </Badge>
              )}
              {lead.mailchimp.source_tag && (
                <Badge variant="outline" className="border-amber-300 bg-white text-amber-900">
                  Canal: {lead.mailchimp.source_tag}
                </Badge>
              )}
            </div>
          </div>
        )}

        {lead.conversation?.length > 0 && (
          <details className="rounded-lg border" data-testid="crmlead-conversation-details">
            <summary className="cursor-pointer px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:bg-muted/40">
              Historial completo del chat con Maya ({lead.conversation.length} mensajes)
            </summary>
            <div className="max-h-72 space-y-2 overflow-y-auto border-t p-3" data-testid="crmlead-conversation">
              {lead.conversation.map((m, i) => (
                <div key={i} className={`text-xs ${m.role === "user" ? "text-right" : ""}`}>
                  <span className={`inline-block max-w-[85%] rounded-lg px-2.5 py-1.5 ${m.role === "user" ? "bg-primary/10" : "bg-muted"} whitespace-pre-wrap`}>{m.content}</span>
                  {m.ts && <div className="mt-0.5 text-[10px] text-muted-foreground">{new Date(m.ts).toLocaleString("es-ES")}</div>}
                </div>
              ))}
            </div>
          </details>
        )}

        {lead.status === "cotizado" && (
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3" data-testid="crmlead-topipeline-box">
            <div className="flex-1 min-w-[160px]">
              <Label className="text-xs">Pasar al Pipeline en la fase…</Label>
              <Select value={phase} onValueChange={setPhase}>
                <SelectTrigger className="mt-1 h-9" data-testid="crmlead-phase-select"><SelectValue /></SelectTrigger>
                <SelectContent>{phases.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button onClick={() => toPipeline.mutate()} disabled={toPipeline.isPending} data-testid="crmlead-topipeline">
              {toPipeline.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-1.5 h-4 w-4" />}Pasar a Pipeline
            </Button>
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <div className="flex gap-2">
            <Button variant="ghost" className="text-red-600" onClick={() => { if (window.confirm("¿Eliminar este lead?")) del.mutate(); }} data-testid="crmlead-delete">
              <Trash2 className="mr-1.5 h-4 w-4" />Eliminar
            </Button>
            <Button variant="ghost" onClick={() => onRemind(lead)} data-testid="crmlead-remind">
              <Clock className="mr-1.5 h-4 w-4" />Recordatorio
            </Button>
            {lead.status !== "archivado" && (
              <Button variant="ghost" onClick={() => setStatus.mutate("archivado")} data-testid="crmlead-archive">
                <Archive className="mr-1.5 h-4 w-4" />Archivar
              </Button>
            )}
          </div>
          <Button
            onClick={() => convert.mutate()}
            disabled={convert.isPending || !!lead.cotizacion_id}
            data-testid="crmlead-convert">
            {convert.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}
            {lead.cotizacion_id ? "Ya cotizado" : "Convertir a Cotización"}<ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchivedDialog({ open, onClose, items }) {
  const qc = useQueryClient();
  const rescue = useMutation({
    mutationFn: async (id) => (await api.patch(`/crm-leads/${id}`, { status: "nuevo" })).data,
    onSuccess: () => { toast.success("Lead rescatado · vuelve a «Nuevo»"); qc.invalidateQueries({ queryKey: ["crm-leads"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="crmlead-archived-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Archive className="h-4 w-4 text-primary" />Leads archivados</DialogTitle>
          <DialogDescription className="text-xs">Rescata un lead para devolverlo a la columna «Nuevo».</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-1.5 overflow-y-auto">
          {items.length === 0 ? (
            <p className="rounded-lg border border-dashed py-8 text-center text-xs text-muted-foreground" data-testid="crmlead-archived-empty">No hay leads archivados.</p>
          ) : items.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2" data-testid={`crmlead-archived-${l.id}`}>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{l.name}</p>
                <p className="truncate text-[11px] text-muted-foreground">{[l.destination, l.source].filter(Boolean).join(" · ") || "—"}{l.opportunity_id ? " · ya en Pipeline" : ""}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => rescue.mutate(l.id)} disabled={rescue.isPending} data-testid={`crmlead-rescue-${l.id}`}>
                <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" />Rescatar
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CualificacionBoard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.has(user?.role);
  const { kanban, pipeline } = useCrmLabels();
  const phases = useMemo(() => [
    { v: "interesado", l: pipeline.interesado },
    { v: "posible_cierre", l: pipeline.posible_cierre },
    { v: "ganado", l: pipeline.ganado },
  ], [pipeline]);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [openLead, setOpenLead] = useState(null);
  const [remindLead, setRemindLead] = useState(null);
  const [archivedOpen, setArchivedOpen] = useState(false);

  const leadsQ = useQuery({ queryKey: ["crm-leads"], queryFn: async () => (await api.get("/crm-leads")).data });
  const leads = leadsQ.data?.items || [];
  const agentsQ = useQuery({
    queryKey: ["crm-lead-agents"],
    queryFn: async () => (await api.get("/crm-leads/agents")).data,
    enabled: isManager,
  });
  const agents = agentsQ.data?.items || [];
  const isNewLead = (lead) => lead.owner_id === user?.id && lead.seen_by_owner === false;
  const seenMut = useMutation({
    mutationFn: async (id) => (await api.post(`/crm-leads/${id}/seen`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["crm-leads"] }); qc.invalidateQueries({ queryKey: ["crm-leads-assigned-new"] }); },
  });
  const openLeadHandler = (lead) => {
    setOpenLead(lead);
    if (isNewLead(lead)) seenMut.mutate(lead.id);
  };
  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLS.map((c) => [c, []]));
    leads.forEach((l) => { if (g[l.status]) g[l.status].push(l); });
    return g;
  }, [leads]);
  const archived = useMemo(() => leads.filter((l) => l.status === "archivado"), [leads]);

  const moveMut = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/crm-leads/${id}`, { status })).data,
    onSuccess: (_, { status }) => { toast.success(`Movido a ${kanban[status]}`); qc.invalidateQueries({ queryKey: ["crm-leads"] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const handleDrop = (status) => {
    setOverCol(null);
    const lead = leads.find((l) => l.id === dragId);
    setDragId(null);
    if (!lead || lead.status === status) return;
    moveMut.mutate({ id: lead.id, status });
  };

  const openReminder = (lead) => { setOpenLead(null); setRemindLead(lead); };
  const liveOpenLead = openLead ? (leads.find((l) => l.id === openLead.id) || openLead) : null;

  return (
    <div className="space-y-4" data-testid="cualificacion-board">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Leads entrantes de la web/Maya. Cualifícalos y, si son buenos, conviértelos en Cotización. Cada lead es ya un cliente unificado.</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setArchivedOpen(true)} data-testid="crmlead-archived-btn"><Archive className="mr-1.5 h-4 w-4" />Archivados{archived.length > 0 ? ` (${archived.length})` : ""}</Button>
          <Button onClick={() => setNewOpen(true)} data-testid="crmlead-new-btn"><Plus className="mr-1.5 h-4 w-4" />Nuevo lead</Button>
        </div>
      </div>

      {leadsQ.isLoading ? <Skeleton className="h-72 w-full" /> : (
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="cualificacion-cols">
          {COLS.map((status) => {
            const list = grouped[status];
            return (
              <section key={status}
                onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverCol(status); } }}
                onDragLeave={(e) => { if (e.currentTarget === e.target) setOverCol(null); }}
                onDrop={() => handleDrop(status)}
                className={`w-64 shrink-0 rounded-xl border bg-muted/20 p-3 transition-colors ${overCol === status ? "border-primary bg-primary/5 ring-2 ring-primary/30" : ""}`}
                data-testid={`crmlead-col-${status}`}>
                <div className="mb-3 flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${DOT[status]}`} />
                  <h3 className="text-xs font-semibold uppercase tracking-wide">{kanban[status]}</h3>
                  <span className="text-xs text-muted-foreground" data-testid={`crmlead-col-count-${status}`}>({list.length})</span>
                </div>
                <div className="flex flex-col gap-2.5">
                  {list.length === 0 && <div className="rounded-lg border border-dashed py-6 text-center text-xs text-muted-foreground">{overCol === status ? "Suelta aquí" : "—"}</div>}
                  {list.map((lead) => (
                    <LeadCard key={lead.id} lead={lead} isNew={isNewLead(lead)} isManager={isManager} onOpen={openLeadHandler}
                      onDragStart={() => setDragId(lead.id)} onDragEnd={() => { setDragId(null); setOverCol(null); }}
                      isDragging={dragId === lead.id} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <NewLeadDialog open={newOpen} onClose={() => setNewOpen(false)} isManager={isManager} agents={agents} />
      <ArchivedDialog open={archivedOpen} onClose={() => setArchivedOpen(false)} items={archived} />
      {openLead && <LeadDrawer lead={liveOpenLead} kanban={kanban} phases={phases} isManager={isManager} agents={agents} onClose={() => setOpenLead(null)} onRemind={openReminder} />}
      <ReminderDialog open={!!remindLead} onClose={() => setRemindLead(null)} entity={remindLead ? { type: "lead", id: remindLead.id, name: remindLead.name, next_action_at: remindLead.next_action_at, next_action_note: remindLead.next_action_note } : null} />
    </div>
  );
}
